import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ShoppingCart, CheckCircle2, AlertCircle, BatteryCharging, Zap, ThermometerSun } from 'lucide-react';
import { useCart } from '@/lib/cartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuoteResultsPage() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/quote-results/${quoteId}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [quoteId]);

  if (loading) return <div className="voltide-container py-20 text-center text-[#57534E]">Generating recommendations...</div>;
  if (!data || data.error) return <div className="voltide-container py-20 text-center"><h2 className="text-2xl font-bold">Quote not found</h2></div>;

  const handleAddBundle = (rec) => {
    addItem({
      sku: rec.sku,
      product_name: rec.product_name,
      pcs_kw: rec.pcs_kw,
      battery_kwh: rec.battery_kwh,
      cooling: rec.cooling,
      image: rec.image,
      standardized_checkout_eligible: rec.standardized_checkout_eligible,
      install_complexity: rec.install_complexity,
    }, 'rent', rec.units);
    toast.success(`${rec.product_name} (x${rec.units}) added to Quote Cart`);
  };

  const tierColors = { Economy: 'emerald', Recommended: 'blue', Premium: 'amber' };

  return (
    <div data-testid="quote-results-page">
      <section className="pt-8">
        <div className="voltide-container">
          <Link to="/quote" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0061DD]" data-testid="back-to-quote">
            <ArrowLeft className="w-4 h-4" /> New Quote
          </Link>
        </div>
      </section>

      <section className="py-12 lg:py-20">
        <div className="voltide-container">
          <div className="max-w-4xl mx-auto">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border-emerald-100 mb-6">
              Quote #{quoteId}
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A1A1A] mb-4">
              Recommended BESS Options
            </h1>

            {/* Input Summary */}
            <div className="bento-card p-5 mb-8">
              <p className="text-sm font-semibold text-[#57534E] mb-2">Based on your inputs:</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="mono"><strong>DG:</strong> {data.input_summary?.dg_rating_kva} KVA</span>
                <span className="mono"><strong>Backup:</strong> {data.input_summary?.backup_hours} hrs</span>
                <span className="mono"><strong>Load:</strong> {data.input_summary?.load_kw} kW</span>
                <span className="mono"><strong>Required:</strong> {data.input_summary?.required_kwh} kWh</span>
                {data.input_summary?.city && <span><strong>City:</strong> {data.input_summary.city}</span>}
              </div>
            </div>

            {/* Recommendations */}
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {data.recommendations?.map((rec, i) => {
                const color = tierColors[rec.tier] || 'blue';
                const isRecommended = rec.tier === 'Recommended';
                return (
                  <div key={rec.sku} className={`bento-card relative overflow-hidden ${isRecommended ? 'ring-2 ring-[#0061DD] shadow-lg' : ''}`} data-testid={`recommendation-${i}`}>
                    {isRecommended && (
                      <div className="bg-[#0061DD] text-white text-xs font-semibold text-center py-1">Best Match</div>
                    )}
                    <div className="p-6">
                      <Badge className={`rounded-full bg-${color}-50 text-${color}-700 border-${color}-100 mb-4`}>{rec.tier}</Badge>
                      <h3 className="text-lg font-bold text-[#1A1A1A] mb-1">{rec.product_name}</h3>
                      <p className="text-sm text-[#57534E] mb-4">{rec.units > 1 ? `${rec.units} units` : '1 unit'}</p>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-2 text-sm">
                          <BatteryCharging className="w-4 h-4 text-[#0061DD]" />
                          <span className="mono">{rec.pcs_kw} kW / {rec.battery_kwh} kWh</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Zap className="w-4 h-4 text-amber-500" />
                          <span className="mono">{rec.backup_hours_achieved} hrs backup</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <ThermometerSun className="w-4 h-4 text-[#57534E]" />
                          <span>{rec.cooling}</span>
                        </div>
                      </div>

                      {/* Confidence */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-[#57534E]">Confidence</span>
                          <span className="mono font-semibold text-[#1A1A1A]">{rec.confidence_score}%</span>
                        </div>
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0061DD] rounded-full" style={{ width: `${rec.confidence_score}%` }} />
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="space-y-2 mb-6">
                        {rec.buy_price_range_inr && (
                          <div className="p-2 rounded-lg bg-stone-50 text-sm">
                            <span className="text-[#57534E]">Buy: </span>
                            <span className="mono font-semibold">{rec.buy_price_range_inr}</span>
                          </div>
                        )}
                        {rec.rent_estimate_range_inr && (
                          <div className="p-2 rounded-lg bg-blue-50/50 text-sm">
                            <span className="text-[#57534E]">Rent: </span>
                            <span className="mono font-semibold text-[#0061DD]">{rec.rent_estimate_range_inr}</span>
                          </div>
                        )}
                      </div>

                      <Button onClick={() => handleAddBundle(rec)} className={`w-full rounded-full gap-2 ${isRecommended ? 'bg-[#0061DD] hover:bg-[#004BB3] text-white' : 'bg-white border border-[#0061DD] text-[#0061DD] hover:bg-blue-50'}`} data-testid={`add-rec-${i}-btn`}>
                        <ShoppingCart className="w-4 h-4" /> Add to Quote Cart
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Improve Confidence */}
            <div className="bento-card p-6">
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                How to improve confidence
              </h3>
              <ul className="space-y-2">
                {data.improve_confidence?.map(item => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#57534E]">
                    <CheckCircle2 className="w-4 h-4 text-[#A8A29E] mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
